import Plotly from 'plotly.js'
import objectHash from 'object-hash'
import { Fragment, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { formatAsBrIntegerNumber, formatAsBrNumber, truthy } from '~/prix'
import Handle from '~/prix/react/components/handle'
import Plot, { NewPlot } from '~/prix/react/components/plot'
import useAction from '~/prix/react/hooks/action'
import { formatDate, parseDate, stringifyDate } from '~/prix/types/date'
import { generateIncidenceDates } from '~/prix/types/presets/time'
import { isEmpty } from '~/prix/utils/empty'
import type { Stats } from '~/utils/server/makeStatsForIncidence'
import actionDefinitions from '../actionDefinitions'
import { Layer, Space } from '~/packages/general/variable/spaceTime.types'
import {
  CalendarPeriodComparisonDetectionResult,
  CalendarPeriodMADDetectionResult,
  ConditionalPolynomialCalendarSeasonalityResult,
  HarmonicResult,
  ProbeResult,
  SeasonalityType,
  TTestOutbreaksResult,
  TimeAnalysisDetectionResult,
  TimeAnalysisLayerResult,
  TimeAnalysisResult,
  getLayerDescriptionsFromResult,
  seasonalityValuesToLabels,
} from '~/packages/general/variable/timeAnalysis/timeAnalysis.data'
import { isDevelopment } from '~/utils/shared/platform'
import TimeAnalysisReportDeveloperTools from './timeAnalysisReportDeveloperTools.component'
import { Probe, TimeSeriesLayer } from '~/packages/general/variable/variables.typing'
import mean from '~/prix/utils/math/mean'
import Holidays from 'date-holidays'
import type { Item } from '~/utils/server/stats/stats.types'
import { buildOptionalStatsActionInput } from './statsActionOptionalInput.util'
import { isVariableLayer } from './clusterListInput.util'
import useAPI from '~/prix/react/hooks/api'
import {
  buildStatsSpaceForUserScope,
  getPreferredSpaceFromGeneralSettings,
} from './userDelimitationScope.util'

const Wrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 80vh;

  h2 {
    margin: 0;
  }

  article {
    height: 80%;
    padding: 2rem;
    main {
      section {
        margin: 2rem 0;

        h3 {
          margin: 0;
        }
        p {
          margin: 10px 0;
        }
        h3,
        p {
          max-width: 800px;
        }
      }

      hr {
        margin: 2rem 0;
        border: 0.1px solid rgba(0, 0, 0, 0.1);
      }
    }
  }
`

interface TimeAnalysisProps {
  focusTimeRange?: {
    start: string
    end: string
  }
  spaceDelimitationId: string | number
  spaceDelimitationType: string
  layer: Layer
}

type ReportTimeAnalysisSummary = {
  x?: string[] | null
  predictionStart: string | null
  predictionY: number[] | null
}

function positionYByDate(y: number[], dates: string[], firstDate: string) {
  const firstDateIndex = dates.findIndex(date => date === firstDate)
  const result: (number | null)[] = new Array(y.length + firstDateIndex).fill(null)
  for (let index = 0; index < y.length; index++) {
    result[firstDateIndex + index] = y[index]
  }
  return result
}

function alignSeriesToDates(
  dates: string[],
  x: string[] | null | undefined,
  series: Array<number | null> | null | undefined,
) {
  if (!series || series.length === 0 || !x || x.length === 0) {
    return new Array(dates.length).fill(null)
  }

  const dateToValueMap = new Map<string, number | null>()
  const xOffset = series.length < x.length ? x.length - series.length : 0
  const seriesOffset = series.length > x.length ? series.length - x.length : 0
  const minLength = Math.min(x.length, series.length)
  for (let index = 0; index < minLength; index++) {
    const xIndex = xOffset + index
    const seriesIndex = seriesOffset + index
    if (xIndex >= x.length || seriesIndex >= series.length) {
      break
    }
    dateToValueMap.set(x[xIndex], series[seriesIndex] as number | null)
  }

  return dates.map(date =>
    dateToValueMap.has(date) ? (dateToValueMap.get(date) as number | null) : null,
  )
}

function generateDateRange(startDate: string, length: number) {
  const result: string[] = []
  const start = parseDate(startDate)
  for (let index = 0; index < length; index++) {
    const next = new Date(start.getTime() + index * 24 * 60 * 60 * 1000)
    result.push(next.toISOString().split('T')[0])
  }
  return result
}

function buildReportDatesString(
  items: Item[],
  componentsResult: ReportTimeAnalysisSummary,
  probesResult: Array<{ predictionStart: string; predictionY: number[] }> = [],
) {
  const dates = new Set<string>()

  items.forEach(item => {
    if (item.dateTime) {
      dates.add(item.dateTime)
    }
  })

  ;((componentsResult.x as unknown as string[]) || []).forEach(date => {
    if (date) {
      dates.add(date)
    }
  })

  if (componentsResult.predictionStart && componentsResult.predictionY?.length) {
    generateDateRange(componentsResult.predictionStart, componentsResult.predictionY.length).forEach(
      date => {
        dates.add(date)
      },
    )
  }

  probesResult.forEach(probeResult => {
    generateDateRange(probeResult.predictionStart, probeResult.predictionY.length).forEach(date => {
      dates.add(date)
    })
  })

  return Array.from(dates).sort((left, right) => left.localeCompare(right))
}

function detectionsToY(
  y: (number | null)[],
  dates: string[],
  detections: Array<{
    start: string
    end: string
  }>,
) {
  const series: (number | null)[] = new Array(y.length).fill(null)
  for (const { start, end } of detections) {
    const startIndex = dates.findIndex(date => date === start)
    const endIndex = dates.findIndex(date => date === end)
    for (let index = startIndex; index <= endIndex; index++) {
      series[index] = y[index]
    }
  }
  return series
}

function getBrazilianHolidays(years: number[]) {
  const hd = new Holidays('BR')
  const brHolidays: { date: string; name: string }[] = []
  years.forEach(year => {
    const holidays = hd.getHolidays(year)
    holidays.forEach(h => {
      if (h.type === 'public') {
        brHolidays.push({ date: h.date.substring(0, 10), name: h.name })
      }
    })
  })
  return brHolidays
}

function formatDateRange(start: string, end: string) {
  return `${formatDate(parseDate(start))} a ${formatDate(parseDate(end))}`
}

function toDetectionHighlightShapes(
  detections: Array<{
    start: string
    end: string
  }>,
  color = 'rgba(231, 76, 60, 0.12)',
) {
  return detections
    .map(detection => {
      try {
        return {
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: parseDate(detection.start),
          x1: parseDate(detection.end),
          y0: 0,
          y1: 1,
          fillcolor: color,
          line: { width: 0 },
        }
      } catch (error) {
        console.error('Invalid detection date range', detection, error)
        return null
      }
    })
    .filter(truthy)
}

interface LayerAnalysis {
  timeSeries?: {
    model?: 'additive' | 'multiplicative'
    fitDays?: number
    layers?: TimeSeriesLayer[]
    probes?: Probe[]
  }
}

export default function TimeAnalysisReport({
  layer,
  spaceDelimitationId,
  spaceDelimitationType,
  focusTimeRange,
}: TimeAnalysisProps) {
  if (!isVariableLayer(layer)) {
    return <output>Análise temporal indisponível para essa camada.</output>
  }

  const theme = useTheme()
  const { context } = useAPI()
  const preferredGeneralSettings = context.user?.activeClient?.settings?.general
  const preferredSpace = useMemo(
    () => getPreferredSpaceFromGeneralSettings(preferredGeneralSettings),
    [
      preferredGeneralSettings?.preferredSpaceChildrenType,
      preferredGeneralSettings?.preferredSpaceDelimitationId,
      preferredGeneralSettings?.preferredSpaceDelimitationTitle,
      preferredGeneralSettings?.preferredSpaceDelimitationType,
    ],
  )
  const { variable } = layer

  const actionDefinition = useMemo(() => {
    const actionDefinition = actionDefinitions.find(
      actionDefinition => actionDefinition.key === variable.actionDefinitionKey,
    )
    if (!actionDefinition) {
      throw new Error(`Action definition not found for variable ${variable.key}`)
    }

    return actionDefinition
  }, [variable.actionDefinitionKey, variable.key])

  const [layerAnalysis, setLayerAnalysis] = useState<LayerAnalysis>(() => ({
    timeSeries: {
      probes: isDevelopment
        ? [
            {
              timeSeriesRelativePosition: 0.5,
              predictionDays: 30,
            },
            {
              timeSeriesRelativePosition: 1,
              predictionDays: 7,
            },
            {
              timeSeriesRelativePosition: 1,
              predictionDays: 30,
            },
            {
              timeSeriesRelativePosition: 1,
              predictionDays: 90,
            },
            {
              timeSeriesRelativePosition: 1,
              predictionDays: 365,
            },
          ]
        : undefined,
      ...variable.timeSeries,
    },
  }))

  const input = useMemo(() => {
    const [rawStartDate] = generateIncidenceDates.last10Years()
    const [, rawEndDate] = generateIncidenceDates.next60DaysPredicted()
    const startDate = stringifyDate(rawStartDate)
    const endDate = stringifyDate(rawEndDate)
    const space =
      spaceDelimitationType && !isEmpty(spaceDelimitationId)
        ? buildStatsSpaceForUserScope(
            {
              delimitationId: spaceDelimitationId,
              delimitationType: spaceDelimitationType,
            } as Space,
            preferredSpace,
          )
        : undefined
    const time = {
      type: 'customDateRangePredicted',
      title: 'Últimos 10 anos e próximos 60 dias (predição)',
      startDate,
      endDate,
      groupType: 'daily',
    }

    return {
      variable,
      ...buildOptionalStatsActionInput({
        space,
        filter: variable.filter,
        highlight: variable.highlight,
        time,
      }),
      analysis: {
        ...layerAnalysis,
        include: {
          prediction: true,
          alerts: true,
          insights: false,
          components: true,
          componentsDetails: true,
          probes: isDevelopment,
        },
      },
    }
  }, [layerAnalysis, preferredSpace, spaceDelimitationId, spaceDelimitationType, variable])

  const requestKey = useMemo(
    () =>
      objectHash(
        {
          actionDefinitionKey: actionDefinition.key,
          input,
        },
        {
          respectType: false,
        },
      ),
    [actionDefinition.key, input],
  )
  const actionResult = useAction(actionDefinition, input, [requestKey])
  const result = actionResult.result as Stats | undefined

  const items = (result?.items || []) as Item[]
  const componentsResult = result?.componentsDetails?.timeSeriesAnalysisResult

  const { mainPlot, layersPlot } = useMemo(() => {
    if (!componentsResult || !items) {
      return {}
    }

    const probes = input.analysis.timeSeries?.probes
    const probeResults =
      componentsResult.probesResult?.filter(
        (probeResult): probeResult is ProbeResult =>
          Boolean(probeResult?.predictionStart && probeResult?.predictionY?.length),
      ) ?? []
    // Une datas agregadas, eixo do modelo e horizontes previstos para não perder a predição.
    const datesString = buildReportDatesString(items, componentsResult, probeResults)
    const dates = datesString.map(stringDate => parseDate(stringDate))

    // Séries alinhadas
    const original = alignSeriesToDates(
      datesString,
      (componentsResult.x as unknown as string[]) || [],
      componentsResult.y as unknown as number[],
    )
    const generated = alignSeriesToDates(
      datesString,
      (componentsResult.x as unknown as string[]) || [],
      (componentsResult.generated as unknown as number[]) || [],
    )
    const residual = alignSeriesToDates(
      datesString,
      (componentsResult.x as unknown as string[]) || [],
      (componentsResult.residual as unknown as number[]) || [],
    )
    const prediction =
      componentsResult.predictionY && componentsResult.predictionStart
        ? alignSeriesToDates(
            datesString,
            generateDateRange(
              componentsResult.predictionStart,
              (componentsResult.predictionY as unknown as number[]).length,
            ),
            componentsResult.predictionY as unknown as number[],
          )
        : new Array(datesString.length).fill(null)

    // Adiciona feriados brasileiros
    const years = Array.from(new Set(datesString.map(date => parseDate(date).getFullYear())))
    const brHolidays = getBrazilianHolidays(years)
    const holidaysOnSeries = brHolidays
      .map(h => {
        const idx = datesString.indexOf(h.date)
        if (idx !== -1) {
          return {
            date: dates[idx],
            name: h.name,
            index: idx,
          }
        }
        return null
      })
      .filter(truthy)

    // Main plot
    const mainPlotData: Plotly.Data[] = [
      {
        x: dates,
        y: residual,
        type: 'scatter' as const,
        mode: 'lines' as const,
        name: 'Resíduo',
        marker: { color: '#bdc3c7' },
      },
      {
        x: dates,
        y: original,
        type: 'scatter' as const,
        mode: 'lines' as const,
        name: 'Original',
        marker: { color: '#3498db' },
      },
      {
        x: dates,
        y: generated,
        type: 'scatter' as const,
        mode: 'lines' as const,
        name: 'Interpretado',
        marker: { color: theme.colors.primary },
      },
      ...(probes || [])
        .map((_probe, index) => {
          const probeResult = componentsResult.probesResult?.[index]
          if (!probeResult) {
            return null
          }

          const y = alignSeriesToDates(
            datesString,
            generateDateRange(probeResult.predictionStart, probeResult.predictionY.length),
            probeResult.predictionY,
          )
          return {
            x: dates,
            y,
            type: 'scatter' as const,
            mode: 'lines' as const,
            name: `Erro ${formatAsBrNumber(probeResult.predictionError)}%`,
            marker: { color: '#d5b6e2' },
          } as Plotly.Data
        })
        .filter(truthy),
      {
        x: dates,
        y: prediction,
        type: 'scatter' as const,
        mode: 'lines' as const,
        name: 'Predição',
        marker: { color: '#9b59b6' },
      },
      // Adiciona feriados
      ...(holidaysOnSeries
        .map((h, idx) => {
          const finiteValues = original.filter(Number.isFinite) as number[]
          if (finiteValues.length === 0) {
            return null
          }

          const yMin = Math.min(...finiteValues)
          const yMax = Math.max(...finiteValues)
          const yRange = yMax - yMin
          const hoverPoints = 10
          const yPoints = Array.from(
            { length: hoverPoints },
            (_, i) => yMin + (yRange * i) / (hoverPoints - 1),
          )

          return {
            x: Array(hoverPoints).fill(h.date),
            y: yPoints,
            type: 'scatter' as const,
            mode: 'lines+markers' as const,
            name: idx === 0 ? 'Feriados' : undefined,
            line: {
              color: '#696969',
              width: 1,
              dash: 'dot',
            },
            marker: {
              size: 0,
              opacity: 0,
            },
            hoverinfo: 'text',
            hovertext: h.name,
            showlegend: idx === 0,
            legendgroup: 'feriados',
            opacity: 0.7,
          } as Plotly.Data
        })
        .filter(truthy) as Plotly.Data[]),
    ]
    const mainPlotResults: string[] = [
      ...(probes
        ? probes.map((probe, index) => {
            const probeResult = componentsResult.probesResult?.[index]
            if (!probeResult) {
              return null
            }

            return `Predição teste de ${probe.predictionDays} dias em ${formatDate(
              parseDate(probeResult.predictionStart),
            )} com erro de predição percentual de ${formatAsBrNumber(
              probeResult.predictionError,
            )}% e erro de geração percentual de ${formatAsBrNumber(probeResult.fitError)}%.`
          })
        : []),

      probes && probes.length && componentsResult.probesResult
        ? `Erro médio entre os testes de predição de ${formatAsBrNumber(
            mean(
              probes
                .map((_probe, index) => componentsResult.probesResult?.[index]?.predictionError)
                .filter((v): v is number => typeof v === 'number'),
            ),
          )}%.`
        : null,
    ].filter(truthy)
    const mainPlot = {
      data: mainPlotData,
      layout: {
        title: { text: 'Série temporal' },
        height: 350,
        xaxis: {
          title: { text: 'Data' },
        },
        yaxis: {
          title: { text: 'Valor' },
        },
        legend: {
          tracegroupgap: 1,
        },
      },
      results: mainPlotResults,
    }

    // Layers plot
    const alreadyPlotted = new Set<string>()
    const resultsArray = (componentsResult.results || []) as TimeAnalysisResult[]
    const layersPlot = resultsArray
      .map((timeAnalysisResult, index) => {
        let charts: NewPlot[]
        const isDetection = Array.isArray((timeAnalysisResult as any).detections)
        const { name, description, settings } = getLayerDescriptionsFromResult(timeAnalysisResult)
        const key = `${name} ${settings}`
        if (
          alreadyPlotted.has(key) &&
          !timeAnalysisResult.name.startsWith('layer.harmonicSeasonality.')
        ) {
          return null
        }
        alreadyPlotted.add(key)
        if (isDetection) {
          const layerInputAligned = alignSeriesToDates(
            datesString,
            (componentsResult.x as unknown as string[]) || [],
            (componentsResult.layersInput?.[index] as unknown as Array<number | null>) || [],
          )
          const detectionResult = timeAnalysisResult as unknown as TimeAnalysisDetectionResult
          const maxDetectionsToPlot = 200
          const limitedDetections =
            detectionResult.detections.length > maxDetectionsToPlot
              ? detectionResult.detections.slice(-maxDetectionsToPlot)
              : detectionResult.detections
          const detectionShapes = toDetectionHighlightShapes(limitedDetections)

          charts = [
            {
              data: [
                {
                  x: dates,
                  y: layerInputAligned,
                  type: 'scatter',
                  mode: 'lines',
                  name: 'Entrada',
                  marker: { color: '#3498db' },
                },
              ],
              layout: {
                title: { text: name },
                height: 350,
                xaxis: {
                  title: { text: 'Data' },
                },
                yaxis: {
                  title: { text: 'Valor' },
                },
                shapes: detectionShapes as any,
              },
            },
          ]

          if (
            (detectionResult as any).type === 'tTestOutbreaks' &&
            Array.isArray((detectionResult as any).pValues)
          ) {
            const tTestOutbreaksResult = detectionResult as unknown as TTestOutbreaksResult
            const alpha = tTestOutbreaksResult.alpha
            const pValuesAligned = alignSeriesToDates(
              datesString,
              (componentsResult.x as unknown as string[]) || [],
              tTestOutbreaksResult.pValues as unknown as Array<number | null>,
            )
            charts.push({
              data: [
                {
                  x: dates,
                  y: pValuesAligned,
                  type: 'scatter',
                  mode: 'lines',
                  name: 'Valor-p',
                  marker: { color: '#9b59b6' },
                },
                {
                  x: [dates[0], dates[dates.length - 1]],
                  y: [alpha, alpha],
                  type: 'scatter',
                  mode: 'lines',
                  name: `Limiar α`,
                  line: {
                    color: '#e74c3c',
                    width: 2,
                    dash: 'dash',
                  },
                },
              ],
              layout: {
                title: { text: 'Valor-p' },
                height: 350,
                xaxis: {
                  title: { text: 'Data' },
                },
                yaxis: {
                  title: { text: 'Valor-p' },
                  range: [0, 1],
                },
              },
            })
          }

          if ((detectionResult as any).type === 'calendarPeriodComparison') {
            const r = detectionResult as unknown as CalendarPeriodComparisonDetectionResult
            const referenceRange = formatDateRange(r.reference.start, r.reference.end)
            const comparisonRange = formatDateRange(r.comparison.start, r.comparison.end)

            const referenceLabel = `Referência`
            const comparisonLabel =
              r.comparedTo === 'previous' ? 'Comparação (anterior)' : 'Comparação (ano anterior)'

            charts.push({
              data: [
                {
                  x: [referenceLabel, comparisonLabel],
                  y: [r.reference.value, r.comparison.value],
                  type: 'bar',
                  marker: { color: ['#3498db', '#95a5a6'] },
                  text: [referenceRange, comparisonRange],
                  hovertemplate: '%{x}<br>%{text}<br>Valor: %{y}<extra></extra>',
                },
              ],
              layout: {
                title: { text: 'Valor do período (agregado)' },
                height: 260,
                xaxis: { title: { text: 'Período' } },
                yaxis: { title: { text: 'Valor' } },
              },
            })

            const referenceY = (r.referenceData?.y || []).filter(v => typeof v === 'number')
            const comparisonY = (r.comparisonData?.y || []).filter(v => typeof v === 'number')
            const length = Math.min(referenceY.length, comparisonY.length)
            if (length > 0) {
              const x = Array.from({ length }, (_, i) => i + 1)
              charts.push({
                data: [
                  {
                    x,
                    y: referenceY.slice(0, length),
                    type: 'scatter',
                    mode: 'lines',
                    name: referenceLabel,
                    marker: { color: '#3498db' },
                  },
                  {
                    x,
                    y: comparisonY.slice(0, length),
                    type: 'scatter',
                    mode: 'lines',
                    name: comparisonLabel,
                    marker: { color: '#95a5a6' },
                  },
                ],
                layout: {
                  title: { text: `Série dentro do período (dias 1..${length})` },
                  height: 260,
                  xaxis: { title: { text: 'Dia no período' } },
                  yaxis: { title: { text: 'Valor' } },
                },
              })
            }
          }

          if ((detectionResult as any).type === 'calendarPeriodMAD') {
            const r = detectionResult as unknown as CalendarPeriodMADDetectionResult
            const historyValues = (r.historyValues || []).filter(Number.isFinite)
            const referenceValue = r.reference?.value
            const lower = r.limits?.lower
            const upper = r.limits?.upper

            if (historyValues.length) {
              const shapes: any[] = []
              if (typeof lower === 'number' && isFinite(lower)) {
                shapes.push({
                  type: 'line',
                  xref: 'paper',
                  x0: 0,
                  x1: 1,
                  yref: 'y',
                  y0: lower,
                  y1: lower,
                  line: { color: '#e67e22', width: 2, dash: 'dash' },
                })
              }
              if (typeof upper === 'number' && isFinite(upper)) {
                shapes.push({
                  type: 'line',
                  xref: 'paper',
                  x0: 0,
                  x1: 1,
                  yref: 'y',
                  y0: upper,
                  y1: upper,
                  line: { color: '#e67e22', width: 2, dash: 'dash' },
                })
              }

              charts.push({
                data: [
                  {
                    y: historyValues,
                    type: 'box' as const,
                    name: 'Histórico',
                    marker: { color: '#95a5a6' },
                    boxpoints: false,
                  },
                  ...(typeof referenceValue === 'number' && isFinite(referenceValue)
                    ? [
                        {
                          x: ['Histórico'],
                          y: [referenceValue],
                          type: 'scatter' as const,
                          mode: 'markers' as const,
                          name: 'Referência',
                          marker: { color: '#e74c3c', size: 10 },
                          hovertemplate: `Referência<br>Valor: %{y}<extra></extra>`,
                        },
                      ]
                    : []),
                ],
                layout: {
                  title: { text: 'Histórico vs limites (MAD)' },
                  height: 300,
                  yaxis: { title: { text: 'Valor do período' } },
                  shapes,
                },
              })
            }
          }

          const results = [
            detectionResult.detections.length
              ? `${detectionResult.detections.length} ${
                  detectionResult.detections.length === 1 ? 'detecção' : 'detecções'
                }`
              : 'Nenhuma detecção',
            detectionResult.detections.length > maxDetectionsToPlot
              ? `Gráfico limitado às últimas ${maxDetectionsToPlot} detecções (por desempenho).`
              : null,
            (detectionResult as any).type === 'sparseData'
              ? (detectionResult as any).isCurrentlySparse
                ? 'Atualmente em período de dados esparsos.'
                : 'Atualmente fora do período de dados esparsos.'
              : null,
            (detectionResult as any).type === 'calendarPeriodComparison'
              ? (() => {
                  const r = detectionResult as unknown as CalendarPeriodComparisonDetectionResult
                  return `Referência: ${formatDateRange(
                    r.reference.start,
                    r.reference.end,
                  )} (${formatAsBrNumber(r.reference.value)}). Comparação: ${formatDateRange(
                    r.comparison.start,
                    r.comparison.end,
                  )} (${formatAsBrNumber(r.comparison.value)}). Variação: ${formatAsBrNumber(
                    r.percentageChange * 100,
                  )}% (limite ${formatAsBrNumber(r.percentageThreshold * 100)}%).`
                })()
              : null,
            (detectionResult as any).type === 'calendarPeriodMAD'
              ? (() => {
                  const r = detectionResult as unknown as CalendarPeriodMADDetectionResult
                  const lower = r.limits?.lower
                  const upper = r.limits?.upper
                  const range =
                    typeof lower === 'number' && typeof upper === 'number'
                      ? `${formatAsBrNumber(lower)} a ${formatAsBrNumber(upper)}`
                      : 'indisponível'
                  return `Referência: ${formatDateRange(
                    r.reference.start,
                    r.reference.end,
                  )} (${formatAsBrNumber(r.reference.value)}). Limites: ${range}. História usada: ${formatAsBrNumber(
                    r.historyUsed,
                  )}.`
                })()
              : null,
          ].filter(truthy)

          return {
            charts,
            name,
            description,
            settings,
            results,
          }
        }

        const timeAnalysisLayerResult = timeAnalysisResult as TimeAnalysisLayerResult
        charts = [
          {
            data: [
              {
                x: dates,
                y: alignSeriesToDates(
                  datesString,
                  (componentsResult.x as unknown as string[]) || [],
                  (componentsResult.layersInput?.[index] as unknown as Array<number | null>) || [],
                ),
                type: 'scatter',
                mode: 'lines',
                name: 'Entrada',
                marker: { color: '#3498db' },
              },
              {
                x: dates,
                y: alignSeriesToDates(
                  datesString,
                  (componentsResult.x as unknown as string[]) || [],
                  (componentsResult.layersGenerated?.[index] as unknown as Array<number | null>) ||
                    [],
                ),
                type: 'scatter',
                mode: 'lines+markers',
                name: 'Saída',
                marker: { color: '#e74c3c' },
              },
            ],
            layout: {
              title: { text: name },
              height: 350,
              xaxis: {
                title: { text: 'Data' },
              },
              yaxis: {
                title: { text: 'Valor' },
              },
            },
          },
        ]

        if (timeAnalysisLayerResult.name.startsWith('layer.calendarSeasonality.')) {
          const calendarSeasonalityResult =
            timeAnalysisLayerResult as ConditionalPolynomialCalendarSeasonalityResult
          const [, , seasonalityType] = calendarSeasonalityResult.name.split('.') as [
            string,
            string,
            SeasonalityType,
          ]

          let keys = Object.keys(calendarSeasonalityResult.meanAmplitudes)
          if (seasonalityType !== 'holiday') {
            keys = keys.sort((a, b) => parseInt(b) - parseInt(a))
          }
          const y = seasonalityValuesToLabels(seasonalityType, keys)
          const x = keys.map(key => calendarSeasonalityResult.meanAmplitudes[key])

          charts.push({
            data: [
              {
                x,
                y,
                type: 'bar' as const,
                orientation: 'h',
                name: 'Impacto médio no modelo',
                marker: { color: '#3498db' },
              },
            ],
            layout: {
              title: { text: 'Impacto médio no modelo' },
              height: x.length * 35 + 110,
              xaxis: {
                title: { text: 'Amplitude média' },
              },
              yaxis: {
                title: { text: 'Sazonalidade' },
                automargin: true,
              },
            },
          })
        }

        const timeAnalysisHarmonicResult = timeAnalysisLayerResult as HarmonicResult
        const results = [
          timeAnalysisLayerResult.changePoints && timeAnalysisLayerResult.changePoints.length
            ? `Ponto de mudança em ${timeAnalysisLayerResult.changePoints
                .map(dateString => formatDate(parseDate(dateString)))
                .join(', ')}`
            : null,
          timeAnalysisHarmonicResult.frequency
            ? `Ciclos de ${formatAsBrNumber(timeAnalysisHarmonicResult.frequency)} dias`
            : null,
          timeAnalysisHarmonicResult.peaks
            ? `${formatAsBrIntegerNumber(timeAnalysisHarmonicResult.peaks)} pico${
                timeAnalysisHarmonicResult.peaks > 1 ? 's' : ''
              } encontrado${timeAnalysisHarmonicResult.peaks > 1 ? 's' : ''}`
            : null,
        ].filter(truthy)

        return {
          charts,
          name,
          description,
          settings,
          results,
        }
      })
      .filter(truthy)

    return {
      mainPlot,
      layersPlot,
    }
  }, [componentsResult, items, input])

  return (
    <Wrapper>
      <article>
        <h2>Relatório de análise de série temporal</h2>

        {isDevelopment ? (
          <TimeAnalysisReportDeveloperTools
            layerAnalysis={layerAnalysis}
            onChangeLayerAnalysis={layerAnalysis => setLayerAnalysis(layerAnalysis)}
          />
        ) : null}

        <Handle error={actionResult.error} isLoading={actionResult.isLoading}>
          <main>
            {mainPlot ? (
              <>
                <Plot data={mainPlot.data} layout={mainPlot.layout} />

                {
                  <ul>
                    {mainPlot.results.map((result, index) => (
                      <li key={index}>{result}</li>
                    ))}
                  </ul>
                }
              </>
            ) : null}
            {layersPlot
              ? layersPlot.map(({ name, description, settings, results, charts }, index) => (
                  <Fragment key={index}>
                    <hr />
                    <section>
                      <h3>{name}</h3>
                      <small>Camada #{(index + 1).toString().padStart(2, '0')}</small>
                      <p>{description}</p>
                      <p>Configuração: {settings ?? 'desconhecida.'}</p>
                      {charts.map(({ data, layout }, index) => (
                        <Plot key={index} data={data} layout={layout} />
                      ))}
                      {
                        <ul>
                          {results.map((result, index) => (
                            <li key={index}>{result}</li>
                          ))}
                        </ul>
                      }
                    </section>
                  </Fragment>
                ))
              : null}
          </main>
        </Handle>
      </article>
    </Wrapper>
  )
}
